import { fromBase64Url } from './bytes.ts';
import type { BindingDocument } from './binding.ts';

/**
 * What an issuer publishes at `https://<issuer>/.well-known/netid.json`
 * (SPEC.md §6): its signing keys and the Concordium accounts that register
 * its documents.
 */
export type IssuerKey = {
  kid: string;
  alg: 'Ed25519';
  /** Raw 32-byte Ed25519 public key, base64url. */
  public_key: string;
  /** RFC 3339; optional validity window. */
  not_before?: string;
  not_after?: string;
  status?: 'active' | 'retired' | 'revoked';
};

/**
 * How the company proved its identity (SPEC.md §8.3):
 *  - `v1-identity`: a Verify & Access (V1) presentation from the identity
 *    credential; the phone verifies it alone, with public identity-provider keys;
 *  - `v0-presentation`: a Web3 ID (V0) presentation from the account
 *    credential; needs that credential's on-chain commitments;
 *  - `id-proof`: the older account ID proof (`requestIdProof`); kept, but no
 *    browser verifier exists for it.
 */
export type ProofStyle = 'v1-identity' | 'v0-presentation' | 'id-proof';

export type CompanyProof = {
  style: ProofStyle;
  /** The exact JSON the wallet returned; `sha256` is over these UTF-8 bytes. */
  json: string;
  sha256: string;
  /** The challenge / context string asked for (= the binding document's SHA-256). */
  challenge: string;
  created_at: string;
  /** V1 only: the transaction that anchored the verification request. */
  request_anchor_tx?: string;
};

/** The account credential's public data (from a node), for a V0 proof. */
export type PinnedCredential = {
  cred_id: string;
  ip_identity: number;
  commitments: Record<string, string>;
  valid_to: string;
  created_at: string;
  /** Where it was read (e.g. "the Concordium wallet's node via grpcTransport"). */
  source: string;
};

export type CompanyBlock = {
  network: 'mainnet' | 'testnet';
  account: string;
  idp: number;
  binding: BindingDocument;
  binding_sha256: string;
  proof: CompanyProof | null;
  /** The RegisterData transaction that carries the binding record. */
  binding_tx: string | null;
  credential?: PinnedCredential | null;
  status: 'pending-onboarding' | 'proof-only' | 'onboarded';
};

export type IssuerDocument = {
  netid: 1 | 2;
  issuer: string;
  name?: string;
  keys: IssuerKey[];
  concordium: { network: 'mainnet' | 'testnet'; accounts: string[] };
  /** v2: the company layer. */
  company?: CompanyBlock;
  /** v2: the DNS TXT record the issuer publishes. */
  dns?: { name: string; txt: string };
};

export function findKey(doc: IssuerDocument, kid: string, now: number): { key: Uint8Array } | { error: string } {
  const k = doc.keys.find((x) => x.kid === kid);
  if (!k) return { error: `key ${kid} is not one of ${doc.issuer}'s published keys` };
  if (k.alg !== 'Ed25519') return { error: `key ${kid} has unsupported alg ${k.alg}` };
  if (k.status === 'revoked') return { error: `key ${kid} is revoked` };
  if (k.not_before && now < Date.parse(k.not_before)) return { error: `key ${kid} is not valid yet` };
  if (k.not_after && now > Date.parse(k.not_after)) return { error: `key ${kid} has expired` };
  const key = fromBase64Url(k.public_key);
  if (key.length !== 32) return { error: `key ${kid} is not 32 bytes` };
  return { key };
}
